import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Eye, Loader2, Search, Trash2, LayoutTemplate, MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { EmptyState } from "@/components/ui/empty-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DocumentList() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch documents from Convex
  const documents = useQuery(api.documents.listDocuments);
  const deleteDocumentMutation = useMutation(api.documents.deleteDocument);

  // Filter documents based on search query
  const filteredDocuments =
    documents?.filter((doc: any) => doc.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
    [];

  // Show loading state while fetching data
  if (documents === undefined) {
    return (
      <DashboardLayout activeTab="document-writer">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  const handleViewDocument = (doc: any) => {
    if (doc.outputType === "google_doc" && doc.googleDocUrl) {
      window.open(doc.googleDocUrl, "_blank");
    } else if (doc.compiledPdfStorageId) {
      // TODO: Implement PDF viewing logic if we have a direct URL,
      // for now navigating to the thread is the safest bet to see the document context
      navigate(`/document-writer`, {
        state: {
          loadThreadId: doc.threadId,
          fromChatHistory: true,
          fromDocuments: true,
        },
      });
    } else {
      // Navigate to document writer thread
      navigate(`/document-writer`, {
        state: {
          loadThreadId: doc.threadId,
          fromChatHistory: true,
          fromDocuments: true,
        },
      });
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocumentMutation({ documentId: documentToDelete as any });
      setDocumentToDelete(null);
    } catch (error) {
      console.error("Failed to delete document:", error);
      alert("Failed to delete document. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
      case "signed":
      case "finalized_ready_for_signature":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>;
      case "generating":
      case "finalizing":
        return (
          <Badge className="flex items-center gap-1 bg-blue-100 text-blue-800 hover:bg-blue-100">
            <Loader2 className="h-3 w-3 animate-spin" /> Generating
          </Badge>
        );
      case "error":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Error</Badge>;
      default:
        return (
          <Badge variant="secondary" className="capitalize">
            {status.replace(/_/g, " ")}
          </Badge>
        );
    }
  };

  return (
    <DashboardLayout activeTab="document-writer">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
            <p className="mt-1 text-gray-500">Manage your created documents</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate("/templates")} className="gap-2">
              <LayoutTemplate className="h-4 w-4" />
              Templates
            </Button>
            <Button
              onClick={() => navigate("/document-writer", { state: { fromDocuments: true } })}
            >
              <FileText className="mr-2 h-4 w-4" />
              Create Document
            </Button>
          </div>
        </div>

        <Card className="w-full p-6">
          <div className="mb-6 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Search documents..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="w-full overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState
                        icon={FileText}
                        title="No documents yet"
                        description="Create your first document to get started."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc: any) => (
                    <TableRow key={doc._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-gray-100 p-2">
                            <FileText className="h-5 w-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {doc.title || "Untitled Document"}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {format(new Date(doc.createdAt), "MMM d, yyyy")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDocumentToDelete(doc._id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <AlertDialog
        open={!!documentToDelete}
        onOpenChange={(open) => !open && setDocumentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the document and remove it
              from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
